'use client';

import { completeNavigationProgress, isTopProgressActive, subscribeTopProgress } from '@/shared/lib/topProgress';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useState, useSyncExternalStore } from 'react';

const INITIAL_PROGRESS = 0.15;
const MAX_TRICKLE_PROGRESS = 0.9;
const TRICKLE_INTERVAL_MS = 250;
const FADE_OUT_MS = 300;

type BarState = { progress: number; isVisible: boolean };

const HIDDEN: BarState = { progress: 0, isVisible: false };

export const TopProgressBar = () => {
  const isActive = useSyncExternalStore(subscribeTopProgress, isTopProgressActive, () => false);
  const [bar, setBar] = useState<BarState>(HIDDEN);
  const pathname = usePathname();
  const search = useSearchParams()?.toString();

  // biome-ignore lint/correctness/useExhaustiveDependencies: URLが変わったことを遷移完了として扱う
  useEffect(() => {
    completeNavigationProgress();
  }, [pathname, search]);

  useEffect(() => {
    if (isActive) {
      setBar((current) => ({ progress: current.isVisible ? Math.max(current.progress, INITIAL_PROGRESS) : INITIAL_PROGRESS, isVisible: true }));
      const timer = setInterval(() => {
        setBar((current) => ({ ...current, progress: current.progress + (MAX_TRICKLE_PROGRESS - current.progress) * 0.1 }));
      }, TRICKLE_INTERVAL_MS);
      return () => clearInterval(timer);
    }
    setBar((current) => (current.isVisible ? { progress: 1, isVisible: false } : current));
    const timer = setTimeout(() => setBar(HIDDEN), FADE_OUT_MS);
    return () => clearTimeout(timer);
  }, [isActive]);

  return (
    <div aria-hidden className="pointer-events-none fixed inset-x-0 top-0 z-[100] h-[2px]">
      <div
        data-top-progress-bar
        className="h-full origin-left bg-primary shadow-[0_0_6px_hsl(var(--primary))]"
        style={{
          transform: `scaleX(${bar.progress})`,
          opacity: bar.isVisible ? 1 : 0,
          transition: bar.progress === 0 ? 'none' : `transform 200ms ease-out, opacity ${bar.isVisible ? 0 : FADE_OUT_MS}ms ease-in`,
        }}
      />
    </div>
  );
};
